import { useCallback } from 'react';
import { get } from 'lodash';
import { useQuery } from 'react-query';
import { ORDERED_INDUSTRIES, PRIMARY_INDUSTRIES } from '@/constants/industries';
import { getPath } from '@/router/utils';
import {
  ROUTE_STOCKS_MARKET,
  ROUTE_STOCKS_MARKET_INDUSTRY,
} from '@/constants/routes';
import { getMarketIndustry } from '@simplywallst/services';

type IndustryOptions = {
  option: string;
  label: string;
  href: string;
}[];

type Query = Record<string | number, unknown>;

export default function useIndustryOptions(
  includeAny = false,
  market = 'global',
  payload?: Record<string | number, string | Query | undefined>
): IndustryOptions {
  const { data: marketIndustries = ORDERED_INDUSTRIES } = useQuery(
    ['market-industry', market],
    async ({ queryKey: [_key, market] }) => {
      const { data } = await getMarketIndustry(market);
      return data.data.map((i) => i.id.toString());
    },
    {
      staleTime: Infinity,
      keepPreviousData: true,
    }
  );

  const industryOptions = useCallback(
    () => [
      ...(includeAny
        ? [
            {
              option: 'any',
              label: 'Any',
              href: getPath({
                type: ROUTE_STOCKS_MARKET,
                payload: { market, ...payload },
              }),
            },
          ]
        : []),
      ...marketIndustries.map((industry) => {
        const industrySlug = get(PRIMARY_INDUSTRIES, [industry, 'slug'], 'any');
        return {
          option: industrySlug,
          label: get(PRIMARY_INDUSTRIES, [industry, 'label'], 'any'),
          href: getPath({
            type: ROUTE_STOCKS_MARKET_INDUSTRY,
            payload: { market, industry: industrySlug, ...payload },
          }),
        };
      }),
    ],
    [market, marketIndustries, payload, includeAny]
  );

  return industryOptions();
}
